import { useState } from "react";
import { useErrorDisplay } from "../components/ErrorDisplay"
import AssessmentClient, { AssessmentErrors, AssessmentDisplay } from "../apiClients/AssessmentClient"
import { useEffect } from "react";
import { useRef } from "react";
import BusySpinner from "../components/BusySpinner";
import { Button, ButtonGroup, Paper, Stack, Tooltip, Typography, alpha } from "@mui/material";
import { ExtractForSetError, FormatDateTime } from "../Helpers";
import AssessmentDisplayPopup from "../popups/AssessmentDisplayPopup";
import PanelSegment from "../components/PanelSegment";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import { useMainDisplayHeight } from "../components/WindowDimensions";
import { useSearchBox } from "../components/SearchBox";
import ConditionalDisplay from "../components/ConditionalDisplay";
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import PageTitle from "../components/PageTitle";
import { I18N } from "../I18N";
import { useAccessInfo } from "../contexts/AccessTokenProvider";
import MobileEntityCard from "../components/responsive/MobileEntityCard";
import ResponsiveCollection from "../components/responsive/ResponsiveCollection";
import useViewportMode from "../hooks/useViewportMode";
import { Securables } from "../Enums";
import {
    FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION_IDS,
    isFinalizedLifecycleReplayOrganization,
} from "../RuntimeConfig";
import FinalizedLifecycleReplayPage from "./FinalizedLifecycleReplayPage";

const AssessmentManagementPage = () => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [assessments, setAssessments] = useState<AssessmentDisplay[]>([]);
    const [busy, setBusy] = useState<boolean>(false);
    const [showFinalizedLifecycleReplay, setShowFinalizedLifecycleReplay] = useState(false);
    const initialized = useRef<boolean>(false);
    const [SearchBox, searchFilter] = useSearchBox();
    const { mode: viewportMode } = useViewportMode();
    const mainDisplayHeight = useMainDisplayHeight();

    const { accessInfo, hasOneOfRoles, hasSubscriptionAccess } = useAccessInfo();
    const canOpenFinalizedLifecycleReplay = hasOneOfRoles([
        Securables.organization_administrator,
        Securables.synergist_admin,
    ], true) && isFinalizedLifecycleReplayOrganization(
        accessInfo?.selectedOrganizationId ?? '',
        accessInfo?.selectedOrganizationName ?? '',
        FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION_IDS,
    );
    const assessmentCollectionHeight = canOpenFinalizedLifecycleReplay
        ? Math.max(320, mainDisplayHeight - 96)
        : mainDisplayHeight;

    const loadData = async () => {
        try {
            setBusy(true);
            
            let apiResponse = await new AssessmentClient().GetAll();
            if(ExtractForSetError(apiResponse, AssessmentErrors.GetAll, setError))
            return;

            setAssessments(apiResponse.Content);

        } catch (error) {
            setError(error, AssessmentErrors.GetAll);
        }
        finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (initialized.current)
            return;

        initialized.current = true;
        loadData();
    });

    const visibleAssessments = searchFilter(assessments) as AssessmentDisplay[];

    const renderAssessmentAction = (assessment: AssessmentDisplay, mobile = false) => hasSubscriptionAccess
        ? <AssessmentDisplayPopup
            assessmentId={assessment.Id}
            hoverContent={`View assessment for ${assessment.FirstName} ${assessment.LastName}`}
            aria-label={`View assessment for ${assessment.FirstName} ${assessment.LastName}`}
        />
        : <Tooltip title="Subscription required">
            <span
                role="img"
                aria-label="Subscription required to view assessment"
                style={mobile ? { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, minHeight: 44 } : undefined}
            >
                <RecentActorsIcon htmlColor={alpha('#919EAB', 0.8)} fontSize="small" />
            </span>
        </Tooltip>;

    const renderAssessmentCard = (assessment: AssessmentDisplay) => (
        <MobileEntityCard
            ariaLabel={`Assessment: ${assessment.LastName}, ${assessment.FirstName}`}
            title={`${assessment.LastName}, ${assessment.FirstName}`}
            subtitle={assessment.TemplateName}
            fields={[
                { label: 'Assessor', value: assessment.AssessorName || '—' },
                { label: 'Created Date', value: FormatDateTime(dayjs(assessment.CreatedDate).toString()) },
            ]}
            actions={renderAssessmentAction(assessment, true)}
        />
    );

    const desktopGrid = (
        <div style={{ height: assessmentCollectionHeight, width: '100%' }}>
            <DataGridPro
                density="compact"
                rows={visibleAssessments.map(x => { return { ...x, id: x.Id } })}
                columns={[
                    {
                        field: 'Action',
                        headerName: '',
                        sortable: false,
                        filterable: false,
                        resizable: false,
                        width: 70,
                        align: 'center',
                        disableColumnMenu: true,
                        renderCell: ({ row: a }) =>
                            <>
                                <ConditionalDisplay ifTrue={hasSubscriptionAccess}>
                                    <ButtonGroup>
                                        <AssessmentDisplayPopup assessmentId={a.Id} />
                                    </ButtonGroup>
                                </ConditionalDisplay>
                                <ConditionalDisplay ifTrue={!hasSubscriptionAccess}>
                                    <ButtonGroup>
                                        <Tooltip title={(hasSubscriptionAccess) ? "" : "Subscription required"}>
                                            <RecentActorsIcon htmlColor={alpha('#919EAB', 0.8)} fontSize='small' />
                                        </Tooltip>
                                    </ButtonGroup>
                                </ConditionalDisplay>
                            </>
                    },
                    {
                        field: 'Talent',
                        headerName: 'Talent',
                        flex: 1,
                        renderCell: ({ row: a }) => `${a.LastName}, ${a.FirstName}`
                    },
                    {
                        field: 'TemplateName',
                        headerName: 'Template',
                        flex: 1
                    },
                    {
                        field: 'AssessorName',
                        headerName: 'Assessor',
                        flex: 1,
                    },
                    {
                        field: 'CreatedDate',
                        headerName: 'Created Date',
                        flex: 1,
                        renderCell: (params) => FormatDateTime(dayjs(params.value).toString())
                    }
                ]}
                components={{
                    Toolbar: GridToolbar
                }}
            />
        </div>
    );

    if (showFinalizedLifecycleReplay) {
        return <Stack spacing={1.5} sx={{ minHeight: 0 }}>
            <Button
                onClick={() => setShowFinalizedLifecycleReplay(false)}
                sx={{ alignSelf: 'flex-start' }}
            >
                Back to completed assessments
            </Button>
            <FinalizedLifecycleReplayPage />
        </Stack>;
    }

    return (
        <>
            <ErrorDisplay />
            <BusySpinner component={Paper} busy={busy}>
                <PanelSegment titleContent={<PageTitle title={I18N.Translate('Completed Assessments')} />} toolbar={<SearchBox />} fillHeight>
                    <Stack spacing={1.5} sx={{ minHeight: 0, height: '100%' }}>
                        {canOpenFinalizedLifecycleReplay && <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
                                <div>
                                    <Typography fontWeight={800}>Jordan Avery · finalized training assessment</Typography>
                                    <Typography variant="body2" color="text.secondary">Synthetic, read-only lifecycle replay for Northstar Field Services Training.</Typography>
                                </div>
                                <Button variant="outlined" onClick={() => setShowFinalizedLifecycleReplay(true)}>
                                    Open finalized Jordan Avery assessment replay
                                </Button>
                            </Stack>
                        </Paper>}
                        <ResponsiveCollection
                            viewMode={viewportMode}
                            items={visibleAssessments}
                            getKey={assessment => assessment.Id}
                            renderCard={renderAssessmentCard}
                            desktop={desktopGrid}
                            ariaLabel="Completed assessments"
                            emptyState={<Typography sx={{ p: 2 }} color="text.secondary">No completed assessments found</Typography>}
                        />
                    </Stack>
                </PanelSegment>
            </BusySpinner>
        </>
    );
}

export default AssessmentManagementPage;
